/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, type HTMLAttributes, createContext, useId } from 'react';
import { type ContextValue, DEFAULT_SLOT, GroupContext as GroupCoreContext, Provider, createLeafComponent, useContextProps } from 'react-aria-components';
import { CloseStrokeIcon } from '../../icons/close-stroke/close-stroke.icon';
import { DeleteStrokeIcon } from '../../icons/delete-stroke/delete-stroke.icon';
import { RotateLeftStrokeIcon } from '../../icons/rotate-left-stroke/rotate-left-stroke.icon';
import { $ } from '../../tokens.stylex';
import { IconButtonContext } from '../icon-button/icon-button.component';
import { IconContext } from '../icon/icon.component';
import { MediaTileContext } from '../media-tile/media-tile.component';
import { ProgressIndicatorContext } from '../progress-indicator/progress-indicator.component';
import { TextContext } from '../text/text.component';
import { ViewContext } from '../view/view.component';

interface UploadListItemProps extends Omit<HTMLAttributes<HTMLLIElement>, 'style' | 'className'> {
  /** Wether the upload list item is used to upload images (Default: false) */
  isCard?: boolean;
  /** Wether the upload list item failed (Default: false) */
  isInvalid?: boolean;
  style?: stylex.StyleXStyles;
}

const UploadListItemContext = createContext<ContextValue<UploadListItemProps, HTMLLIElement>>({});

const UploadListItem = createLeafComponent('item', (props: UploadListItemProps, ref: ForwardedRef<HTMLLIElement>) => {
  [props, ref] = useContextProps(props, ref, UploadListItemContext);

  const { style, children, isCard = false, isInvalid = false, ...coreProps } = props;

  const id = useId();

  const errorMessageId = `${id}-error-message`;

  return (
    <li
      ref={ref}
      aria-describedby={isInvalid ? errorMessageId : undefined}
      {...stylex.props(styles.base, isCard && styles.card, isInvalid && styles.invalid, style)}
      {...coreProps}>
      {(() => {
        return (
          <Provider values={[
            [MediaTileContext, { style: mediaTileStyles.base }],
            [ViewContext, { style: [viewStyles.base, isCard && viewStyles.card] }],
            [ProgressIndicatorContext, {
              size: 16,
              variant: 'interactive',
            }],
            [TextContext, {
              slots: {
                [DEFAULT_SLOT]: {
                  size: isCard ? 16 : 14,
                  color: 'primary',
                  style: [textStyles.base, textStyles.truncate],
                },
                [UPLOAD_LIST_ITEM_SLOTS.DESCRIPTION]: {
                  size: 14,
                  weight: 'regular',
                  color: 'quaternary',
                  style: [textStyles.base, isCard && textStyles.truncate],
                },
                [UPLOAD_LIST_ITEM_SLOTS.PROGRESS_VALUE]: {
                  size: 13,
                  weight: 'regular',
                  color: 'quaternary',
                  style: [textStyles.base, isCard && textStyles.truncate],
                  'aria-hidden': true
                },
                [UPLOAD_LIST_ITEM_SLOTS.ERROR_MESSAGE]: {
                  size: 13,
                  weight: 'regular',
                  color: 'critical',
                  style: [textStyles.base, isCard && textStyles.truncate],
                  id: errorMessageId,
                  role: 'status',
                  children: 'File upload error',
                  'aria-live': 'polite',
                  'aria-atomic': 'true',
                },
              }
            }],
            [IconButtonContext, {
              slots: {
                [UPLOAD_LIST_ITEM_SLOTS.RETRY]: {
                  size: 24,
                  variant: 'ghost',
                  "aria-label": "Retry upload",
                  style: (iconButtonRenderProps) => iconButtonRenderProps.isDisabled && iconButtonStyles.disabled,
                  children: <RotateLeftStrokeIcon />,
                },
                [UPLOAD_LIST_ITEM_SLOTS.DELETE]: {
                  size: 24,
                  variant: 'ghost',
                  "aria-label": "Delete upload",
                  style: (iconButtonRenderProps) => iconButtonRenderProps.isDisabled && iconButtonStyles.disabled,
                  children: <DeleteStrokeIcon />,
                },
                [UPLOAD_LIST_ITEM_SLOTS.CANCEL]: {
                  size: 24,
                  variant: 'ghost',
                  "aria-label": "Cancel upload",
                  style: (iconButtonRenderProps) => iconButtonRenderProps.isDisabled && iconButtonStyles.disabled,
                  children: <CloseStrokeIcon />,
                },
              }
            }],
            [GroupCoreContext, stylex.props(groupCoreStyles.base)],
            [IconContext, {
              slots: {
                [UPLOAD_LIST_ITEM_SLOTS.ICON_STATUS]: {
                  "aria-hidden": true,
                  size: isCard ? undefined : 16,
                  isFilled: true,
                  children: isInvalid
                    ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M10.0001 18.1818C14.5187 18.1818 18.1819 14.5187 18.1819 9.99999C18.1819 5.4813 14.5187 1.81818 10.0001 1.81818C5.48136 1.81818 1.81824 5.4813 1.81824 9.99999C1.81824 14.5187 5.48136 18.1818 10.0001 18.1818ZM9.09096 6.36363C9.09096 5.86155 9.49798 5.45454 10.0001 5.45454C10.5021 5.45454 10.9091 5.86155 10.9091 6.36363V9.99999C10.9091 10.5021 10.5021 10.9091 10.0001 10.9091C9.49798 10.9091 9.09096 10.5021 9.09096 9.99999V6.36363ZM11.1364 13.6364C11.1364 14.264 10.6277 14.7727 10.0001 14.7727C9.37246 14.7727 8.86369 14.264 8.86369 13.6364C8.86369 13.0088 9.37246 12.5 10.0001 12.5C10.6277 12.5 11.1364 13.0088 11.1364 13.6364Z" fill="#CD3535" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M8.18182 16.3636C12.7005 16.3636 16.3636 12.7005 16.3636 8.18182C16.3636 3.66312 12.7005 0 8.18182 0C3.66312 0 0 3.66312 0 8.18182C0 12.7005 3.66312 16.3636 8.18182 16.3636ZM12.461 6.09737C12.816 5.74235 12.816 5.16674 12.461 4.81172C12.106 4.4567 11.5304 4.4567 11.1754 4.81172L6.36364 9.62344L5.18828 8.44808C4.83326 8.09306 4.25765 8.09306 3.90263 8.44808C3.54761 8.80311 3.54761 9.37871 3.90263 9.73373L5.72081 11.5519C6.07583 11.9069 6.65144 11.9069 7.00646 11.5519L12.461 6.09737Z" fill="#288344" />
                      </svg>
                    )
                }
              }
            }]
          ]}>
            {children}
          </Provider>
        );
      })()}
    </li>
  );
});

const styles = stylex.create({
  base: {
    gap: 8,
    overflow: 'hidden',
    alignContent: 'center',
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    maxWidth: '100%',
  },
  card: {
    borderColor: $.border16,
    borderRadius: 8,
    borderStyle: 'solid',
    borderWidth: 1,
    gap: 12,
    paddingBlock: 4,
    paddingLeft: 4,
    paddingRight: 14,
  },
  invalid: {
    borderColor: $.borderCritical,
  }
});

const viewStyles = stylex.create({
  base: {
    gap: 6,
    overflow: 'hidden',
    alignItems: 'center',
    display: 'flex',
    flexBasis: 0,
    flexGrow: 1,
    flexShrink: 0,
    maxWidth: '100%',
    minWidth: 0,
  },
  card: {
    gap: 2,
    alignItems: 'flex-start',
    flexDirection: 'column',
  }
});

const mediaTileStyles = stylex.create({
  base: {
    height: 80,
    width: 80,
  },
});

const textStyles = stylex.create({
  base: {
    whiteSpace: 'nowrap',
  },
  truncate: {
    overflow: 'hidden',
    display: 'block',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
  }
});

const iconButtonStyles = stylex.create({
  disabled: {
    backgroundColor: 'transparent'
  }
});

const groupCoreStyles = stylex.create({
  base: {
    gap: 12,
    alignContent: 'center',
    alignItems: 'center',
    display: 'flex',
  }
});

const UPLOAD_LIST_ITEM_SLOTS = {
  DESCRIPTION: 'description',
  PROGRESS_VALUE: 'progress-value',
  ERROR_MESSAGE: 'error-message',
  ICON_STATUS: 'icon-status',
  RETRY: 'retry',
  DELETE: 'delete',
  CANCEL: 'cancel',
};

export {
  UPLOAD_LIST_ITEM_SLOTS,
  UploadListItem,
  UploadListItemContext,
  type UploadListItemProps
};
